// ============================================================
// DM-LMS — Courses Context
// Fetches GET /api/courses?page=1&pageSize=100 exactly once per mount
// and shares the result with every consumer below the provider, so
// tutor/student pages that all need the course list don't each trigger
// their own request for the same data (was: 6 simultaneous requests
// on page load, tripping the API's rate limiter).
// ============================================================

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useFetch } from "../hooks/useApi.js";
import { useAuth } from "../hooks/useAuth.js";
import type { Course, PaginatedResult } from "@dm-lms/shared/types";

interface CoursesContextValue {
  courses:   Course[];
  isLoading: boolean;
  error:     string | null;
}

const CoursesContext = createContext<CoursesContextValue | null>(null);

export function CoursesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data, isLoading, error } = useFetch<PaginatedResult<Course>>("/api/courses?page=1&pageSize=100");

  // Tutors only ever act on their own courses — filtered here, once, instead
  // of by every consuming page.
  const courses = useMemo(() => {
    const items = data?.items ?? [];
    return user?.role === "tutor" ? items.filter((course) => course.tutorId === user.id) : items;
  }, [data, user]);

  const value = useMemo(() => ({ courses, isLoading, error }), [courses, isLoading, error]);

  return <CoursesContext.Provider value={value}>{children}</CoursesContext.Provider>;
}

export function useCourses(): CoursesContextValue {
  const ctx = useContext(CoursesContext);
  if (!ctx) throw new Error("useCourses must be used within a CoursesProvider");
  return ctx;
}
